import React, { useState, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import "wicg-inert";

import Portal from "components/Portal";
import useScrollLock from "hooks/useScrollLock";

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  /* Por encima del header (z-index: 1000): sin esto el header le pintaba
     encima y el modal aparecía cortado por arriba, además de quedar fuera del
     oscurecido. */
  z-index: 1100;
  background-color: rgba(51, 51, 51, 0.45);
  backdrop-filter: blur(2px);
  opacity: 0;
  transition: all 100ms cubic-bezier(0.4, 0, 0.2, 1);
  transition-delay: 200ms;
  display: flex;
  align-items: center;
  justify-content: center;

  & .modal-content {
    transform: translateY(100px);
    transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
    opacity: 0;
    border-radius: 10px;
  }

  &.active {
    transition-duration: 250ms;
    transition-delay: 0ms;
    opacity: 1;

    & .modal-content {
      transform: translateY(0);
      opacity: 1;
      transition-delay: 150ms;
      transition-duration: 350ms;
    }
  }
`;

const Content = styled.div`
  position: relative;
  /* overflow-x oculto para clipear el contenido a las esquinas redondeadas;
     overflow-y auto para que un form más alto que la pantalla scrollee acá
     adentro en vez de quedar cortado (el scroll del body está bloqueado). */
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;
  min-height: 50px;
  min-width: 50px;
  max-height: 90%;
  /* El borde marca el límite del modal cuando lo que hay atrás también es
     blanco (el header, por ejemplo): con sólo la sombra, el canto de arriba
     se perdía. */
  border: 1px solid #c9d4e4;
  box-shadow: 0 18px 48px rgba(8, 37, 90, 0.28),
    0 4px 12px rgba(8, 37, 90, 0.16);
  background-color: ${"#ffffff"};
  border-radius: 12px;

  /* Por default el modal se ajusta al contenido (con cap de 90%).
     wide=true: matcheo el ancho del ListOf (95% con max 124.5rem),
     ideal para formularios largos. */
  ${({ wide }) =>
    wide
      ? css`
          width: 95%;
          max-width: 124.5rem;
        `
      : css`
          max-width: 90%;
        `}
`;

export default function Modal({ open, onClose, locked, wide, children }) {
  const [active, setActive] = useState(false);

  const backdrop = useRef(null);

  useScrollLock(open);

  useEffect(() => {
    const { current } = backdrop;

    const transitionEnd = () => setActive(open);

    const keyHandler = (e) =>
      !locked && [27].indexOf(e.which) >= 0 && onClose();

    const clickHandler = (e) => !locked && e.target === current && onClose();

    if (current) {
      current.addEventListener("transitionend", transitionEnd);
      current.addEventListener("click", clickHandler);
      window.addEventListener("keyup", keyHandler);
    }

    if (open) {
      window.setTimeout(() => {
        document.activeElement.blur();
        setActive(open);
        document.querySelector("#root").setAttribute("inert", "true");
      }, 10);
    }

    return () => {
      if (current) {
        current.removeEventListener("transitionend", transitionEnd);
        current.removeEventListener("click", clickHandler);
      }

      document.querySelector("#root").removeAttribute("inert");
      window.removeEventListener("keyup", keyHandler);
    };
  }, [open, locked, onClose]);

  return (
    <React.Fragment>
      {(open || active) && (
        <Portal className="modal-portal">
          <Backdrop ref={backdrop} className={active && open && "active"}>
            <Content className="modal-content" wide={wide}>
              {children}
            </Content>
          </Backdrop>
        </Portal>
      )}
    </React.Fragment>
  );
}
